import { useMutation } from '@apollo/client';
import { useSnackbar } from 'notistack';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { DeleteProviderDocument } from '../../../graphql/mutation/providers/DeleteProvider.generated.tsx';

export const useProviderDelete = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const [providerName, setProviderName] = useState<string | null>(null);

  const [deleteProviderMutation, { loading, error }] = useMutation(DeleteProviderDocument, {
    onCompleted: response => {
      enqueueSnackbar(
        t('Provider {providerId} successfully deleted.', {
          providerId: providerName ?? response.delete_provider_by_pk?.id,
        }),
        { variant: 'success' }
      );

      navigate('/providers/');
    },
    onError: () => {
      enqueueSnackbar(t('Failed to delete provider.'), { variant: 'error' });
    },
    refetchQueries: ['GetProvidersList'],
  });

  const deleteProvider = useCallback(
    async (id: string, name: string): Promise<void> => {
      try {
        setProviderName(name);
        await deleteProviderMutation({ variables: { id } });
      } catch (err) {
        // biome-ignore lint/suspicious/noConsole: logging error in console
        console.error('Error deleting provider:', err);
      } finally {
        setProviderName(null);
      }
    },
    [deleteProviderMutation]
  );

  return {
    deleteProvider,
    error,
    loading,
  };
};
